import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PatternFormat } from "react-number-format";
import { Form, Formik } from "formik";
import type { FormikHelpers } from "formik";
import * as yup from "yup";
import dayjs from "dayjs";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useAppDispatch, useAppSelector } from "../store";
import planoApi from "../api/plano.api";
import telemedicinaApi from "../api/telemedicina.api";
import { UpdadeCadastro } from "../store/actions/usuario";
import Section from "../components/Section";
import SectionSkeleton from "../components/SectionSkeleton";
import { formatCelular } from "../helpers";
import enqueueSnackbar from "../helpers/enqueueSnackbar";
import telemedicinaImage from "../assets/telemedicina.png";

const dataNascimentoRegex = /^(\d{2})\s{1}\/\s{1}(\d{2})\s{1}\/\s{1}(\d{4})$/;

type UpdateClienteValues = {
  cpf?: string;
  nome?: string;
  dataNascimento?: string;
  sexo?: string;
  email?: string;
  celular?: string;
};

type ClienteErrors = {
  cpf?: string;
  nome?: string;
  dataNascimento?: string;
  sexo?: string;
  email?: string;
  celular?: string;
};

const StyledButton = styled(Button)({
  position: "relative",

  "& .StyledButton-CircularProgress": {
    position: "absolute",
    height: "100%",
    width: "100%",
    left: 0,
    top: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
});

function UpdateClienteSection({
  clienteErrors,
  onSuccess,
}: {
  clienteErrors: ClienteErrors;
  onSuccess: () => void;
}) {
  const validationSchema = yup.object({
    cpf: clienteErrors?.cpf
      ? yup
          .string()
          .matches(/^\d{11}$/, "Campo inválido")
          .required("Campo obrigatório")
      : yup.string().optional().nullable().strip(),
    nome: clienteErrors?.nome
      ? yup.string().required("Campo obrigatório")
      : yup.string().optional().nullable().strip(),
    dataNascimento: clienteErrors?.dataNascimento
      ? yup
          .string()
          .matches(dataNascimentoRegex, "Campo inválido")
          .test("dataNascimentoValido", "Campo inválido", (value) =>
            dayjs(value.replace(dataNascimentoRegex, "$3-$2-$1")).isValid()
          )
          .required("Campo obrigatório")
      : yup.string().optional().nullable().strip(),
    sexo: clienteErrors?.sexo
      ? yup
          .string()
          .oneOf(["F", "M"], "Campo obrigatório")
          .required("Campo obrigatório")
      : yup.string().optional().nullable().strip(),
    celular: clienteErrors?.celular
      ? yup
          .string()
          .matches(/(^0?\d{2})(\d{5})(\d{4})/, "Campo inválido")
          .required("Campo obrigatório")
      : yup.string().optional().nullable().strip(),
    email: clienteErrors?.email
      ? yup.string().email("Campo inválido").required("Campo obrigatório")
      : yup.string().optional().nullable().strip(),
  });

  const onSubmit = async (
    values: UpdateClienteValues,
    helpers: FormikHelpers<UpdateClienteValues>
  ) => {
    try {
      helpers.setStatus(null);

      if (!clienteErrors.cpf) delete values.cpf;
      if (!clienteErrors.nome) delete values.nome;
      if (!clienteErrors.dataNascimento) {
        delete values.dataNascimento;
      } else if (values.dataNascimento) {
        values.dataNascimento = values.dataNascimento.replace(
          dataNascimentoRegex,
          "$3-$2-$1"
        );
      }
      if (!clienteErrors.sexo) delete values.sexo;
      if (!clienteErrors.email) delete values.email;
      if (!clienteErrors.celular) delete values.celular;

      await UpdadeCadastro(values);

      helpers.setStatus("success");

      enqueueSnackbar(
        "Seu cadastro foi atualizado. Você será redirecionado em alguns instantes...",
        "success"
      );

      onSuccess();
    } catch (error) {
      helpers.setStatus("error");

      enqueueSnackbar(
        error?.message ||
          "Não foi possível atualizar seu cadastro. Verifique os dados e tente novamente.",
        "error"
      );
    }
  };

  return (
    <Formik
      initialValues={{
        cpf: "",
        nome: "",
        dataNascimento: "",
        sexo: "",
        email: "",
        celular: "",
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({
        errors,
        touched,
        values,
        isSubmitting,
        handleBlur,
        handleChange,
        setFieldValue,
        status,
      }) => {
        const getHelperText = (name: keyof ClienteErrors) =>
          touched[name] && !!errors[name] ? errors[name] : undefined;

        return (
          <Form>
            <Stack spacing={2}>
              <Typography>
                Para iniciar sua consulta, é necessário que complete algumas
                informações do seu cadastro.
              </Typography>

              {!!clienteErrors?.cpf && (
                <PatternFormat
                  label="CPF"
                  name="cpf"
                  value={values.cpf}
                  valueIsNumericString
                  onValueChange={(values) => setFieldValue("cpf", values.value)}
                  onBlur={handleBlur}
                  format="###.###.###-##"
                  placeholder="000.000.000-00"
                  mask="_"
                  allowEmptyFormatting
                  error={touched.cpf && !!errors.cpf}
                  helperText={getHelperText("cpf")}
                  fullWidth
                  customInput={TextField}
                />
              )}

              {!!clienteErrors.nome && (
                <TextField
                  label="Nome"
                  name="nome"
                  value={values.nome}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.nome && !!errors.nome}
                  helperText={getHelperText("nome")}
                  fullWidth
                />
              )}

              {!!clienteErrors.sexo && (
                <FormControl>
                  <FormLabel>Sexo</FormLabel>
                  <RadioGroup
                    name="sexo"
                    value={values.sexo}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    row
                  >
                    <FormControlLabel
                      value="F"
                      control={<Radio />}
                      label="Female"
                    />
                    <FormControlLabel
                      value="M"
                      control={<Radio />}
                      label="Male"
                    />
                  </RadioGroup>
                  {touched.sexo && !!errors.sexo && (
                    <FormHelperText error>{errors.sexo}</FormHelperText>
                  )}
                </FormControl>
              )}

              {!!clienteErrors.dataNascimento && (
                <PatternFormat
                  label="Data de nascimento"
                  name="dataNascimento"
                  value={values.dataNascimento}
                  onValueChange={(values) =>
                    setFieldValue("dataNascimento", values.formattedValue)
                  }
                  onChange={handleChange}
                  onBlur={handleBlur}
                  format="## / ## / ####"
                  placeholder="Dia / Mês / Ano"
                  mask="_"
                  allowEmptyFormatting
                  error={touched.dataNascimento && !!errors.dataNascimento}
                  helperText={getHelperText("dataNascimento")}
                  fullWidth
                  customInput={TextField}
                />
              )}

              {!!clienteErrors.email && (
                <TextField
                  label="E-mail"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && !!errors.email}
                  helperText={getHelperText("email")}
                  fullWidth
                />
              )}

              {!!clienteErrors?.celular && (
                <TextField
                  label="Celular"
                  name="celular"
                  value={formatCelular(values.celular)}
                  onChange={(event) =>
                    setFieldValue(
                      "celular",
                      event.target.value.replace(/\D+/gi, "")
                    )
                  }
                  onBlur={handleBlur}
                  error={touched.celular && !!errors.celular}
                  helperText={getHelperText("celular")}
                  fullWidth
                />
              )}

              <StyledButton
                variant="contained"
                color="primary"
                size="large"
                type="submit"
                disabled={isSubmitting || status === "success"}
              >
                Atualizar
                {isSubmitting && (
                  <div className="StyledButton-CircularProgress">
                    <CircularProgress />
                  </div>
                )}
              </StyledButton>
            </Stack>
          </Form>
        );
      }}
    </Formik>
  );
}

function QuestionarioSection({
  navigate,
}: {
  navigate: ReturnType<typeof useNavigate>;
}) {
  const [values, setValues] = useState<{ observations?: string }>({});
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [url, setUrl] = useState<string | null>(null);

  const handleCreateAtendimento = async () => {
    try {
      setSubmitting(true);

      const { url } = await telemedicinaApi.clicklifeCreateAtendimento({
        observations: values?.observations,
      });

      setUrl(url);

      enqueueSnackbar(
        "Você será atendido em breve! Clique o botão abaixo para seguir ao atendimento.",
        "success"
      );
    } catch (error) {
      enqueueSnackbar(
        error?.message ||
          "Não foi possível prosseguir com a consulta. Tente novamente.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openAtendimento = () => {
    window.open(url, "blank");

    navigate("/app/minha-conta");
  };

  return (
    <>
      <Typography>
        Antes de iniciar a consulta, você deseja adicionar uma observação?
      </Typography>

      <Box sx={{ width: "100%", maxWidth: { lg: "30rem" } }}>
        <TextField
          name="observations"
          label="Observações"
          onChange={(event) =>
            setValues({ ...values, observations: event.target.value })
          }
          multiline
          fullWidth
          rows={4}
          inputProps={{ maxLength: 500 }}
        />

        <Typography sx={{ width: "100%", mt: 1, pr: 2, textAlign: "right" }}>
          {values?.observations?.length || 0} / 500
        </Typography>
      </Box>

      {!!url ? (
        <Button
          variant="contained"
          color="success"
          size="large"
          onClick={() => openAtendimento()}
        >
          Abrir consulta
        </Button>
      ) : (
        <StyledButton
          variant="contained"
          color="primary"
          size="large"
          onClick={() => handleCreateAtendimento()}
          disabled={submitting}
        >
          Prosseguir
          {submitting && (
            <div className="StyledButton-CircularProgress">
              <CircularProgress />
            </div>
          )}
        </StyledButton>
      )}
    </>
  );
}

export default function TelemedicinaPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    loading: loadingCliente,
    cliente,
    plano,
  } = useAppSelector((state) => state.cliente);
  const [loadingPlano, setLoadingPlano] = useState<boolean>(true);

  const [authenticating, setAuthenticating] = useState<boolean>(false);
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const [clienteErrors, setClienteErrors] = useState<ClienteErrors | null>(
    null
  );

  const loadPlano = async (abortController?: AbortController) => {
    try {
      if (plano) {
        setLoadingPlano(false);

        return;
      }

      if (loadingCliente || !cliente) {
        return;
      }

      setLoadingPlano(true);

      const clientePlano = await planoApi.findById(
        cliente.planoId,
        abortController
      );

      dispatch({ type: "SET_CLIENTE_PLANO", payload: clientePlano });
    } catch (error) {
      if (error?.code === "ERR_CANCELED") {
        return;
      }

      enqueueSnackbar(
        error?.message || "Não foi possível obter as informações do seu plano.",
        "error"
      );
    } finally {
      setLoadingPlano(false);
    }
  };

  const handleAuth = async () => {
    try {
      setAuthenticating(true);

      await telemedicinaApi.clicklifeAuth();

      setAuthenticated(true);
    } catch (error) {
      if (error?.validationError) {
        setClienteErrors(error.validationError);
      } else {
        enqueueSnackbar(error.message, "error");
      }
    } finally {
      setAuthenticating(false);
    }
  };

  useEffect(() => {
    const abortController = new AbortController();

    if (plano) {
      setLoadingPlano(false);
    }

    if (!loadingCliente && cliente && !plano) {
      loadPlano(abortController);
    }

    return () => {
      abortController.abort();
    };
  }, [loadingCliente, cliente, plano]);

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexFlow: "column nowrap",
        alignItems: "center",
        gap: theme.spacing(2),
        height: "100%",
      })}
    >
      <Box sx={{ textAlign: "center", pb: 2 }}>
        <Typography variant="h4">Telemedicina</Typography>
        <Typography>Tenha rapidez e segurança no seu atendimento.</Typography>
      </Box>

      {loadingCliente || loadingPlano ? (
        <SectionSkeleton />
      ) : (
        <Section
          sx={(theme) => ({
            display: "flex",
            flexFlow: "column nowrap",
            alignItems: "center",
            gap: theme.spacing(2),
            height: "100%",
          })}
        >
          {!authenticated && (
            <Box
              sx={{
                maxHeight: "300px",
                maxWidth: "300px",
              }}
            >
              <img
                src={telemedicinaImage}
                alt="Figura de diagnóstico pelo celular"
              />
            </Box>
          )}

          {!plano || !plano.telemedicina ? (
            <>
              <Typography sx={{ textAlign: "center" }}>
                Parece que o serviço de telemedicina não está incluso no seu
                plano.
              </Typography>

              <Typography sx={{ fontWeight: "bold", textAlign: "center" }}>
                Assine já e tenha acesso imediato a todos os benefícios!
              </Typography>

              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => navigate("/app/assinar")}
              >
                Quero Assinar
              </Button>
            </>
          ) : !clienteErrors ? (
            <StyledButton
              variant="contained"
              color="primary"
              size="large"
              onClick={() => handleAuth()}
              disabled={authenticating}
            >
              Iniciar consulta
              {authenticating && (
                <div className="StyledButton-CircularProgress">
                  <CircularProgress />
                </div>
              )}
            </StyledButton>
          ) : !!clienteErrors ? (
            <UpdateClienteSection
              clienteErrors={clienteErrors}
              onSuccess={() => {
                setClienteErrors(null);
                handleAuth();
              }}
            />
          ) : authenticated ? (
            <QuestionarioSection navigate={navigate} />
          ) : (
            <></>
          )}
        </Section>
      )}
    </Box>
  );
}
